import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api';
import EmployeeAddModal from './modals/Employee/EmployeeAddModal';
import Pagination from '../Tools/Pagination';

const EmployeeTab = () => {
  // State for employee data management
  const [employees, setEmployees] = useState([]); // Stores list of employees
  const [employeeIds, setEmployeeIds] = useState([]); // Stores all employee IDs for filtering
  const [specialities, setSpecialities] = useState([]); // Stores speciality options

  // Modal and form state management
  const [showModal, setShowModal] = useState(false); // Controls modal visibility
  const [formData, setFormData] = useState({
    emp_id: '',
    first_name: '',
    last_name: '',
    speciality: '',
    email: '',
    phone_number: '',
    available_status: 1,
    case_assigned: '',
    location_id: '',
    area_id: '',
  });

  // Edit mode and loading states
  const [isEditMode, setIsEditMode] = useState(false); // Tracks if in edit mode
  const [isLoading, setIsLoading] = useState(false); // Loading state for API calls
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null); // Current employee being edited

  // Pagination state
  const [page, setPage] = useState(1); // Current page number
  const [limit] = useState(10); // Items per page (fixed)
  const [total, setTotal] = useState(0); // Total number of employees

  // Filter state
  const [filterForm, setFilterForm] = useState({
    emp_id: '',
    first_name: '',
    speciality: ''
  });

  // Helper function to display alerts
  const showAlert = (message, isSuccess = true) => {
    alert(`${isSuccess ? 'Success:' : 'Error:'} ${message}`);
  };

  /**
   * Handles filter input changes and updates filter state
   * @param {Object} e - The event object from the input field
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterForm(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Submits filters and fetches filtered employee data
   * @param {Object} e - Optional event object from form submission
   */
  const handleFilterSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/employees', {
        params: {
          page,
          limit,
          emp_id: filterForm.emp_id,
          first_name: filterForm.first_name,
          speciality: filterForm.speciality
        },
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json'
        }
      });
      
      const data = response.data?.data || response.data;
      setEmployees(Array.isArray(data) ? data : (data.data || []));
      setTotal(data.total || data.length || 0);
    } catch (error) {
      console.error('Filter error:', error);
      showAlert('Failed to filter employees', false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clears all filters and resets to initial state
   */
  const handleClearFilter = () => {
    setFilterForm({ emp_id: '', first_name: '', speciality: '' });
    setPage(1);
    fetchInitialData();
  };

  /**
   * Fetches initial employee data, employee IDs, and specialities
   */
  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch paginated employee data
      const response = await axiosInstance.get('/employees', {
        params: { page, limit },
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json'
        }
      });
      const data = response.data?.data || response.data;
      setEmployees(Array.isArray(data) ? data : (data.data || []));
      setTotal(data.total || data.length || 0);

      // Fetch all employee IDs for filtering
      const idsResponse = await axiosInstance.get('/employees', { 
        params: { limit: 1000 },
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json'
        }
      });
      const idsData = idsResponse.data?.data || idsResponse.data;
      setEmployeeIds(idsData.map(emp => emp.emp_id));

      // Fetch speciality options
      const specialitiesResponse = await axiosInstance.get('/specialities', {
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json'
        }
      });
      setSpecialities(specialitiesResponse.data.data || specialitiesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showAlert('Failed to load data', false);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect for handling filter changes with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filterForm.emp_id || filterForm.first_name || filterForm.speciality) {
        handleFilterSubmit();
      } else {
        fetchInitialData();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [filterForm, page]);

  /**
   * Handles form input changes for employee data
   * @param {Object} e - The event object from the input field
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Opens the modal in add mode with empty form
   */
  const openAddModal = () => {
    setIsEditMode(false);
    setFormData({
      emp_id: '',
      first_name: '',
      last_name: '',
      speciality: '',
      email: '',
      phone_number: '',
      available_status: 1,
      case_assigned: '',
      location_id: '',
      area_id: ''
    });
    setShowModal(true);
  };

  /**
   * Opens the modal in edit mode with employee data
   * @param {Object} emp - The employee object to edit
   */
  const handleEdit = (emp) => {
    setIsEditMode(true);
    setCurrentEmployeeId(emp.id);
    setFormData({
      emp_id: emp.emp_id,
      first_name: emp.first_name,
      last_name: emp.last_name,
      speciality: emp.speciality?.id || emp.speciality || '',
      email: emp.email,
      phone_number: emp.phone_number,
      available_status: emp.available_status,
      case_assigned: emp.case_assigned || '',
      location_id: emp.location?.id || '',
      area_id: emp.area?.id || '',
    });
    setShowModal(true);
  };

  /**
   * Saves employee data (both create and update)
   */
  const handleSave = async () => {
    try {
      setIsLoading(true);
      const token = sessionStorage.getItem('auth_token');

      // Prepare form data for API request
      const payload = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value || '');
      });

      // Make API call based on edit mode
      if (isEditMode) {
        await axiosInstance.put(`/employees/${currentEmployeeId}`, payload, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
        });
        showAlert('Employee updated successfully!');
      } else {
        await axiosInstance.post('/employees', payload, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
        });
        showAlert('Employee added successfully!');
      }

      // Refresh data and close modal
      setShowModal(false);
      fetchInitialData();
    } catch (error) {
      console.error('Save failed:', error.response?.data || error.message);
      showAlert(
        error.response?.data?.message ||
          `Failed to ${isEditMode ? 'update' : 'add'} employee`,
        false
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Deletes an employee after confirmation
   * @param {number} id - The ID of the employee to delete
   */
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        setIsLoading(true);
        const token = sessionStorage.getItem('auth_token');
        await axiosInstance.delete(`/employees/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        showAlert('Employee deleted successfully!');
        fetchInitialData();
      } catch (error) {
        console.error('Delete failed:', error.response?.data || error.message);
        showAlert('Failed to delete employee', false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Filter employee IDs based on input (for potential autocomplete)
  const filteredEmpIds = employeeIds.filter(id =>
    id.toLowerCase().includes(filterForm.emp_id.toLowerCase())
  );

  // Filter specialities based on input (for potential autocomplete)
  const filteredSpecialities = specialities.filter(spec =>
    spec.speciality_name.toLowerCase().includes(filterForm.speciality.toLowerCase())
  );

  return (
    <div className="tab-pane active" id="employee" role="tabpanel">
      {/* Filter Section */}
      <div className="filter-search mt-5">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            {/* Employee ID Filter */}
            <div className="form-group col-lg-3 p-2">
              <input
                autoComplete="off"
                type="text"
                className="form-control"
                name="emp_id"
                placeholder="Employee ID"
                value={filterForm.emp_id}
                onChange={handleFilterChange}
              />
            </div>

            {/* Name Filter */}
            <div className="form-group col-lg-3 p-2">
              <input
                autoComplete="off"
                type="text"
                className="form-control"
                name="first_name"
                placeholder="First Name"
                value={filterForm.first_name}
                onChange={handleFilterChange}
              />
            </div>

            {/* Speciality Filter */}
            <div className="form-group col-lg-3 p-2 position-relative">
              <select
                className="form-control"
                name="speciality"
                value={filterForm.speciality}
                onChange={handleFilterChange}
              >
                <option value="">All Specialities</option>
                {specialities.map((spec) => (
                  <option key={spec.id} value={spec.speciality_name}>
                    {spec.speciality_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filter Button */}
            <div className="form-group col-lg-3 p-2">
              <button 
                type="button" 
                className="btn mb-1 waves-effect waves-light btn-rounded btn-outline-primary"
                onClick={handleClearFilter}
              >
                Clear Filter
              </button>
            </div>
          </form>

          {/* Add Employee Button */}
          <button
            className="btn my-2 d-flex align-items-center justify-content-center btn-outline-primary waves-effect waves-light"
            onClick={openAddModal}
          >
            <i className="ti ti-circle-plus fs-4 me-2"></i>
            Add Employee
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      {isLoading ? (
        <div className="text-center py-4">Loading employees...</div>
      ) : (
        <>
          {/* Employee Table */}
          <div className="py-3 px-0">
            <div className="table-responsive rounded-2 ros-table">
              <table className="table text-nowrap customize-table mb-0 align-middle">
                <thead className="text-dark fs-4">
                  <tr>
                    <th><h6 className="fw-semibold mb-0">Employee ID</h6></th>
                    <th><h6 className="fw-semibold mb-0">Name</h6></th>
                    <th><h6 className="fw-semibold mb-0">Email</h6></th>
                    <th><h6 className="fw-semibold mb-0">Phone</h6></th>
                    <th><h6 className="fw-semibold mb-0">Speciality</h6></th>
                    <th><h6 className="fw-semibold mb-0">Locations</h6></th>
                    <th><h6 className="fw-semibold mb-0">Areas</h6></th>
                    <th><h6 className="fw-semibold mb-0">Status</h6></th>
                    <th><h6 className="fw-semibold mb-0">Case</h6></th>
                    <th><h6 className="fw-semibold mb-0">Actions</h6></th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length > 0 ? (
                    employees.map((emp) => (
                      <tr key={emp.id}>
                        <td>{emp.emp_id}</td>
                        <td>{emp.first_name} {emp.last_name}</td>
                        <td>{emp.email}</td>
                        <td>{emp.phone_number}</td>
                        <td>{emp.speciality?.speciality_name || 'Null'}</td>
                        <td>{emp.location?.location_name || 'Null'}</td>
                        <td>{emp.area?.area_name || 'Null'}</td>
                        <td>
                          <span className={`round-8 rounded-circle me-2 d-inline-block ${emp.available_status ? 'text-bg-success' : 'text-bg-danger'}`}></span>
                          {emp.available_status ? 'Available' : 'Busy In Case'}
                        </td>
                        <td>{emp.case_assigned || 'Null'}</td>
                        <td>
                          <button 
                            className="h4 me-3 border-0 bg-transparent" 
                            onClick={() => handleEdit(emp)}
                            title="Edit Employee"
                          >
                            <i className="ti ti-edit text-warning"></i>
                          </button>
                          <button 
                            className="h4 border-0 bg-transparent" 
                            onClick={() => handleDelete(emp.id)}
                            title="Delete Employee"
                          >
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-4">No employees found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Component */}
          <Pagination
            total={total}
            limit={limit}
            page={page}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Employee Add/Edit Modal */}
      <EmployeeAddModal
        show={showModal}
        onClose={() => setShowModal(false)}
        formData={formData}
        onChange={handleInputChange}
        onSave={handleSave}
        isEditMode={isEditMode}
        isLoading={isLoading}
      />
    </div>
  );
};

export default EmployeeTab;